"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import {
  readConsentSnapshot,
  saveConsentPreferences,
  subscribeConsentChanges,
} from "@/lib/consent";

function getConsentSnapshot() {
  return JSON.stringify(readConsentSnapshot());
}

export function ConsentPreferences() {
  const serializedSnapshot = useSyncExternalStore(subscribeConsentChanges, getConsentSnapshot, () => null);
  const snapshot = serializedSnapshot ? JSON.parse(serializedSnapshot) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);
  const firstControlRef = useRef(null);
  const preferencesButtonRef = useRef(null);
  const showDialog = Boolean(snapshot && (!snapshot.hasChoice || isOpen));

  useEffect(() => {
    if (showDialog && isCustomizing) firstControlRef.current?.focus();
  }, [isCustomizing, showDialog]);

  useEffect(() => {
    if (!showDialog) {
      delete document.body.dataset.consentPreferencesOpen;
      return undefined;
    }

    document.body.dataset.consentPreferencesOpen = "true";
    return () => {
      delete document.body.dataset.consentPreferencesOpen;
    };
  }, [showDialog]);

  function persistPreferences(nextAnalytics, nextAdvertising) {
    const nextSnapshot = saveConsentPreferences({
      analytics: nextAnalytics,
      advertising: nextAdvertising,
    });

    setAnalytics(nextSnapshot.analytics);
    setAdvertising(nextSnapshot.advertising);
    setIsCustomizing(false);
    setIsOpen(false);
    window.setTimeout(() => preferencesButtonRef.current?.focus(), 0);
  }

  function openPreferences() {
    const currentSnapshot = readConsentSnapshot();
    setAnalytics(currentSnapshot.analytics);
    setAdvertising(currentSnapshot.advertising);
    setIsCustomizing(true);
    setIsOpen(true);
  }

  function closePreferences() {
    if (!snapshot?.hasChoice) return;
    setIsCustomizing(false);
    setIsOpen(false);
    window.setTimeout(() => preferencesButtonRef.current?.focus(), 0);
  }

  if (!snapshot) return null;

  return (
    <>
      <button
        ref={preferencesButtonRef}
        type="button"
        onClick={openPreferences}
        data-bottom-floating-control
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-4 z-40 rounded-full border border-slate-300 bg-white px-4 py-2 text-[13px] font-semibold text-slate-800 shadow-lg transition-[bottom,background-color] duration-200 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
      >
        Privacy preferences
      </button>

      {showDialog ? (
        <div
          className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-5"
          onKeyDown={(event) => {
            if (event.key === "Escape") closePreferences();
          }}
        >
          <section
            role="dialog"
            aria-labelledby="consent-title"
            aria-describedby="consent-description"
            className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="consent-title" className="text-lg font-bold text-slate-950">
                  Your privacy preferences
                </h2>
                <p id="consent-description" className="mt-2 text-sm leading-6 text-slate-600">
                  We use optional analytics to improve this website and optional advertising
                  tools to understand campaign results. You can change your choice at any time.
                  Read our{" "}
                  <Link
                    href="/privacy-policy"
                    className="font-semibold text-sky-700 underline underline-offset-2 hover:text-sky-800"
                  >
                    privacy policy
                  </Link>
                  .
                </p>
              </div>
              {snapshot.hasChoice ? (
                <button
                  type="button"
                  onClick={closePreferences}
                  aria-label="Close privacy preferences"
                  className="shrink-0 rounded-lg px-3 py-2 text-xl leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                >
                  ×
                </button>
              ) : null}
            </div>

            {isCustomizing ? (
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-900">Essential</span>
                    <span className="text-[13px] font-semibold text-emerald-700">Always active</span>
                  </div>
                  <p className="mt-2 text-[13px] leading-5 text-slate-600">
                    Required for security, forms, and remembering your privacy choice.
                  </p>
                </div>

                <label className="cursor-pointer rounded-xl border border-slate-200 p-4 transition-colors hover:border-sky-300">
                  <span className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-900">Analytics</span>
                    <input
                      ref={firstControlRef}
                      type="checkbox"
                      checked={analytics}
                      onChange={(event) => setAnalytics(event.target.checked)}
                      className="h-5 w-5 accent-sky-600"
                    />
                  </span>
                  <span className="mt-2 block text-[13px] leading-5 text-slate-600">
                    Helps us understand visits and improve useful content.
                  </span>
                </label>

                <label className="cursor-pointer rounded-xl border border-slate-200 p-4 transition-colors hover:border-sky-300">
                  <span className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-slate-900">Advertising</span>
                    <input
                      type="checkbox"
                      checked={advertising}
                      onChange={(event) => setAdvertising(event.target.checked)}
                      className="h-5 w-5 accent-sky-600"
                    />
                  </span>
                  <span className="mt-2 block text-[13px] leading-5 text-slate-600">
                    Lets us measure ads from services such as Google, Meta, and X.
                  </span>
                </label>
              </div>
            ) : null}

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                onClick={() => persistPreferences(false, false)}
                className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
              >
                Reject optional
              </button>
              {isCustomizing ? (
                <button
                  type="button"
                  onClick={() => persistPreferences(analytics, advertising)}
                  className="rounded-xl border border-sky-700 px-4 py-3 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                >
                  Save preferences
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsCustomizing(true)}
                  className="rounded-xl border border-sky-700 px-4 py-3 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                >
                  Customize
                </button>
              )}
              <button
                type="button"
                onClick={() => persistPreferences(true, true)}
                className="rounded-xl bg-sky-700 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
              >
                Accept all
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
